<template>
  <div class="p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">{{ $t('Mes statistiques') }}</h1>
        <p class="text-gray-500">{{ $t('Suivi de votre activite') }}</p>
      </div>
      <router-link
        to="/stockist-dashboard"
        class="px-4 py-2 border rounded-lg hover:bg-gray-100"
      >
        {{ $t('Retour') }}
      </router-link>
    </div>

    <!-- Date Range Filter -->
    <div class="bg-white rounded-lg shadow p-4 mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Date debut') }}</label>
          <input
            type="date"
            v-model="filters.startDate"
            class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">{{ $t('Date fin') }}</label>
          <input
            type="date"
            v-model="filters.endDate"
            class="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="flex items-end">
          <button
            @click="loadStats"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {{ $t('Appliquer') }}
          </button>
        </div>
        <div class="flex items-end gap-2">
          <button
            @click="setDateRange('today')"
            class="px-3 py-2 border rounded-lg hover:bg-gray-100 text-sm"
          >
            {{ $t('Aujourd\'hui') }}
          </button>
          <button
            @click="setDateRange('week')"
            class="px-3 py-2 border rounded-lg hover:bg-gray-100 text-sm"
          >
            {{ $t('Cette semaine') }}
          </button>
          <button
            @click="setDateRange('month')"
            class="px-3 py-2 border rounded-lg hover:bg-gray-100 text-sm"
          >
            {{ $t('Ce mois') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Summary Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-500 text-sm">{{ $t('Total colis affectes') }}</p>
            <p class="text-3xl font-bold text-blue-600">{{ stats.totals ? stats.totals.assigned : 0 }}</p>
          </div>
          <div class="p-3 rounded-full bg-blue-100 text-blue-600">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
            </svg>
          </div>
        </div>
        <p class="text-sm text-gray-400 mt-2">{{ formatPeriod() }}</p>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-500 text-sm">{{ $t('Total retours valides') }}</p>
            <p class="text-3xl font-bold text-green-600">{{ stats.totals ? stats.totals.returnsValidated : 0 }}</p>
          </div>
          <div class="p-3 rounded-full bg-green-100 text-green-600">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
        </div>
        <p class="text-sm text-gray-400 mt-2">{{ formatPeriod() }}</p>
      </div>
    </div>

    <!-- Daily Activity Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="p-4 border-b">
        <h3 class="text-lg font-semibold">{{ $t('Activite quotidienne') }}</h3>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{{ $t('Date') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Total') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Prepares') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Expedies') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Livres') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Retours') }}</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{{ $t('Refuses') }}</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="7" class="px-4 py-8 text-center">
                <div class="flex justify-center items-center">
                  <svg class="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span class="ml-2">{{ $t('Chargement...') }}</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="!stats.dailyStats || stats.dailyStats.length === 0">
              <td colspan="7" class="px-4 py-8 text-center text-gray-500">
                {{ $t('Aucune donnee disponible pour cette periode') }}
              </td>
            </tr>
            <tr v-else v-for="day in stats.dailyStats" :key="day._id" class="hover:bg-gray-50">
              <td class="px-4 py-3 font-medium text-gray-900">
                {{ formatDateDisplay(day._id) }}
              </td>
              <td class="px-4 py-3 text-center font-semibold">
                {{ day.total }}
              </td>
              <td class="px-4 py-3 text-center">
                <span class="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">
                  {{ getStatusCount(day, 'prepared') }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  {{ getStatusCount(day, 'shipped') }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                  {{ getStatusCount(day, 'delivered') }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-sm">
                  {{ getStatusCount(day, 'return') + getStatusCount(day, 'return validated') }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <span class="px-2 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                  {{ getStatusCount(day, 'refused') }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MyStats',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      loading: false,
      stats: {},
      filters: {
        startDate: '',
        endDate: ''
      }
    };
  },
  mounted() {
    this.setDateRange('week');
  },
  watch: {
    // Reload when warehouse changes in header
    idWharhouse: {
      handler: function() {
        this.loadStats();
      },
      deep: true
    }
  },
  methods: {
    async loadStats() {
      this.loading = true;
      try {
        const params = {};

        // Use selected country from header
        if (this.idWharhouse && this.idWharhouse.country) {
          params.country = this.idWharhouse.country;
        }

        if (this.filters.startDate) {
          params.startDate = this.filters.startDate;
        }
        if (this.filters.endDate) {
          params.endDate = this.filters.endDate;
        }

        const res = await this.$server.getMyStats('stockistDashboard', params);
        if (res && res.content) {
          this.stats = res.content;
        } else if (res && !res.content) {
          this.stats = res;
        }
      } catch (error) {
        console.error('Error loading stats:', error);
      }
      this.loading = false;
    },

    setDateRange(range) {
      const today = new Date();
      const endDate = new Date(today);
      let startDate = new Date(today);

      switch (range) {
        case 'today':
          // Same day
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(startDate.getMonth() - 1);
          break;
      }

      this.filters.startDate = this.formatDateForInput(startDate);
      this.filters.endDate = this.formatDateForInput(endDate);
      this.loadStats();
    },

    formatDateForInput(date) {
      return date.toISOString().split('T')[0];
    },

    formatPeriod() {
      if (this.stats.period) {
        const start = this.$moment(this.stats.period.startDate).format('DD/MM/YYYY');
        const end = this.$moment(this.stats.period.endDate).format('DD/MM/YYYY');
        return `${start} - ${end}`;
      }
      return '';
    },

    formatDateDisplay(dateStr) {
      return this.$moment(dateStr).format('DD/MM/YYYY');
    },

    getStatusCount(day, status) {
      if (!day.statuses) return 0;
      const found = day.statuses.find(function(s) { return s.status === status; });
      return found ? found.count : 0;
    }
  }
};
</script>

<style scoped>
</style>
